// functions/warehouse_page/warehouse_page_functions.jsx

// Import API routes
import { download_warehouse_charges, fetch_all_warehouse_costs, invoice_warehouse_charges } from "../../api/warehouse";
import { fetchEnhancedLanes, fetchAllProducts, inboundProducts, downloadHistoricalStockReport, fetchAllPallets } from "../../api/enhancedWarehouse";
import { fetch_all_customers } from "../../api/customers";
import { check_pending_orders } from "../../api/notifications";
import { getCustomerWarehouseSummary } from "../../api/customerWarehouseSummary";
import { validSession } from "../../components/lib/validSession";
import { sortByNumber, sortList } from "../../components/lib/functions";
import { generateEnhancedQRCode } from "../../api/enhancedWarehouse";
import { getSessionUUID } from '../../components/lib/AuthToken';
import logger from "../../utils/logger";

export class WarehousePageFunctions {

    constructor(showAlert) {
        this.showAlert = showAlert;
    }

    async loadPageData(setIsLoading, setCustomers, setSelectedCustomer, setWarehouseLanes, setWarehouseProducts, setPendingOrdersCount, restricted, customerRestricted, logout, navigate, profile, isCustomer, setWarehouseSummaryProducts, setAllCustomerPallets, currentSelectedCustomer = null, setCustomerName, setAllPallets) {
        
        const isSuccessful = (status) => [200, 201].includes(status);
        setIsLoading(true);
        if (!validSession()) {
            setIsLoading(false);
            return this.showAlert({
                message: 'Session expired, please login again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/');
                        logger.log("Session expired, failed to fetch profiles");
                    },
                }],
            });
        }
        
        try {
            // Don't set loading here - let the parent component handle it
            
            // Determine if we need customer-specific data
            const customerId = isCustomer && profile?.customer_id ? profile.customer_id : null;
            
            // Handle selectedCustomer setting based on user type
            if (isCustomer && customerId) {
                // For customer users, set selectedCustomer to their customer ID
                const targetCustomerId = customerId;
                if (setSelectedCustomer && currentSelectedCustomer !== targetCustomerId) {
                    setSelectedCustomer(targetCustomerId);
                } else {
                    logger.log('✅ Selected customer already set correctly for customer user:', currentSelectedCustomer);
                }
            } else {
                // For admin/warehouse users, preserve the current selectedCustomer (don't reset to null)
                logger.log('✅ Preserving selected customer for admin/warehouse user:', currentSelectedCustomer);
                // Don't call setSelectedCustomer - let it maintain its current value
            }

            if (isCustomer && customerId) {
                // For customer users, fetch their specific warehouse data
                setCustomerName(profile?.name || ''); // Set customer name for display
                
                try {
                    await this.loadSelectedCustomerData(parseInt(customerId), setIsLoading, setWarehouseLanes, setWarehouseProducts, setWarehouseSummaryProducts, null);
                } catch (error) {
                    logger.error('❌ Error loading customer warehouse summary:', error);
                    setWarehouseLanes({});
                    setWarehouseProducts([]);
                    setCustomers([]);
                    setPendingOrdersCount(0);
                    if (setWarehouseSummaryProducts) setWarehouseSummaryProducts([]);
                    if (setAllCustomerPallets) setAllCustomerPallets([]);
                }
                
                // Early return for customer users - don't load admin/warehouse data
                setIsLoading(false);
                return;
            } else {
                // For non-customer users (admin/warehouse), clear customer name initially and fetch customers, warehouse costs, and pending orders
                logger.log('🔄 Loading admin/warehouse data - fetching customers, costs, and pending orders');
                if (setCustomerName) {
                    setCustomerName(null); // Clear customer name for non-customer users initially
                }
                
                const promises = [
                    fetch_all_customers().catch(error => {
                        logger.error('Failed to fetch customers:', error);
                        return { status: 500, data: { data: [] }, error: error.message };
                    }),
                    fetch_all_warehouse_costs().catch(error => {
                        logger.error('Failed to fetch warehouse costs:', error);
                        return { status: 500, data: [], error: error.message };
                    }),
                    check_pending_orders().catch(error => {
                        logger.error('Failed to fetch pending orders:', error);
                        return { status: 500, count: 0, error: error.message };
                    }),
                    fetchAllPallets().catch(error => {
                        logger.error('Failed to fetch all pallets:', error);
                        return { status: 500, data: [], error: error.message };
                    })
                ]; 

                const [customersResponse, warehouseCostsResponse, pendingOrdersResponse, allPalletsResponse] = await Promise.all(promises);

                // Process customers and filter by warehouse costs
                if (isSuccessful(customersResponse.status)) {
                    // all customers need to be sorted by id first (numeric sort)
                    const sortedCustomers = sortByNumber(customersResponse.data.data, 'id');
                    
                    // Only proceed if warehouse costs were loaded successfully
                    if (isSuccessful(warehouseCostsResponse.status)) {
                        // Validate warehouse costs data structure
                        let warehouseCosts = [];
                        if (warehouseCostsResponse.data) {
                            if (Array.isArray(warehouseCostsResponse.data)) {
                                warehouseCosts = warehouseCostsResponse.data;
                            } else if (warehouseCostsResponse.data.data && Array.isArray(warehouseCostsResponse.data.data)) {
                                warehouseCosts = warehouseCostsResponse.data.data;
                            } else {
                                logger.warn('⚠️ Warehouse costs data is not in expected array format:', typeof warehouseCostsResponse.data, warehouseCostsResponse.data);
                                warehouseCosts = [];
                            }
                        }
                        
                        
                        // Create a set of customer IDs that have warehouse costs
                        const customerIdsWithCosts = new Set(warehouseCosts.map(cost => cost.customer_id));
                        
                        // Filter customers to only those with warehouse costs
                        const customersWithCosts = sortedCustomers.filter(customer => 
                            customerIdsWithCosts.has(customer.id)
                        );
                    
                        // now create a set to filter out duplicates based on company
                        const uniqueCompanies = new Set();
                        const uniqueCustomers = customersWithCosts.filter(customer => {
                            if (uniqueCompanies.has(customer.company)) {
                                return false;
                            } else {
                                uniqueCompanies.add(customer.company);
                                return true;
                            }
                        });
                        // finally sort the unique customers by company name
                        const uniqueSorted = sortList(uniqueCustomers, 'company');
                        setCustomers(uniqueSorted);
                    } else {
                        logger.warn('⚠️ Failed to load warehouse costs, showing all customers');
                        logger.warn('⚠️ Warehouse costs error:', warehouseCostsResponse.error || 'Unknown error');
                        const sortedAllCustomers = sortList(sortedCustomers, 'company');
                        setCustomers(sortedAllCustomers);
                    }
                } else {
                    logger.error('❌ Failed to load customers - Response status:', customersResponse.status);
                    logger.error('❌ Customers response error:', customersResponse.error || 'Unknown error');
                    setCustomers([]);
                    logger.warn('⚠️ Failed to load customers');
                }
                
                // Process pending orders (with better error handling)
                if (isSuccessful(pendingOrdersResponse.status)) {
                    const pendingCount = pendingOrdersResponse.count || 0;
                    setPendingOrdersCount(pendingCount);
                } else {
                    logger.warn('⚠️ Failed to load pending orders:', pendingOrdersResponse.error || 'Server error');
                    setPendingOrdersCount(0);
                }

                if (isSuccessful(allPalletsResponse.status)) {
                    const allPalletsData = allPalletsResponse.data || [];
                    setAllPallets(allPalletsData);
                } else {
                    setAllPallets([]); 
                }
            }
            
            setIsLoading(false);
            logger.log('✅ Warehouse page data loading completed successfully');
        } catch (error) {
            logger.error('❌ Error loading warehouse page data:', error);
            logger.error('❌ Error stack:', error.stack);
            logger.error('❌ Error message:', error.message);
            setIsLoading(false);
            this.showAlert({
                message: 'Failed to load warehouse data',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }
    }

    // async handleDownloadCharges() {
    //     try {
    //         await download_warehouse_charges();
    //         this.showAlert({
    //             message: 'Warehouse charges downloaded successfully',
    //             type: 'success',
    //             buttons: [{ label: 'Close', type: 'primary' }]
    //         });
    //     } catch (error) {
    //         logger.error('Error downloading charges:', error);
    //         this.showAlert({
    //             message: 'Failed to download warehouse charges',
    //             type: 'danger',
    //             buttons: [{ label: 'Close', type: 'primary' }]
    //         });
    //     }
    // }

    async loadSelectedCustomerData(customerId, setIsLoading, setWarehouseLanes, setWarehouseProducts, setAllCustomerPallets = null, abortSignal = null) {
        /**
         * Load warehouse data for a specific customer when selected from dropdown
         * This is called when admin/warehouse users select a customer
         */
        if (!customerId) {
            setWarehouseLanes({});
            setWarehouseProducts([]);
            if (setAllCustomerPallets) {
                setAllCustomerPallets([]); // Clear stored customer pallets when no customer selected
            }
            return;
        }

        setIsLoading(true);
        
        try {
            logger.log('🔄 Loading warehouse data for selected customer:', customerId);
            
            // Check if request was aborted before making API call
            if (abortSignal && abortSignal.aborted) {
                throw new DOMException('Request was aborted', 'AbortError');
            }
            
            const warehouseSummaryResult = await getCustomerWarehouseSummary(parseInt(customerId), abortSignal);
            
            // Check if request was aborted after API call
            if (abortSignal && abortSignal.aborted) {
                throw new DOMException('Request was aborted', 'AbortError');
            }
            
            if (warehouseSummaryResult.success && warehouseSummaryResult.data) {
                logger.log('✅ Selected customer warehouse summary loaded:', warehouseSummaryResult.data);
                
                // Transform the API response to match the component's expected format
                const transformedLanes = {};
                
                // Convert products object to array if it's an object with numbered keys
                let transformedProducts = [];
                if (warehouseSummaryResult.data.products) {
                    if (Array.isArray(warehouseSummaryResult.data.products)) {
                        transformedProducts = warehouseSummaryResult.data.products;
                    } else {
                        // Convert object with numbered keys to array
                        transformedProducts = Object.values(warehouseSummaryResult.data.products);
                    }
                }
                
                logger.log('🔄 Transformed products to array:', transformedProducts.length, 'products');
                if (transformedProducts.length > 0) {
                    logger.log('🔍 Sample transformed product:', transformedProducts[0]);
                }
                
                // If we have pallet data in the summary, organize it into lanes
                if (warehouseSummaryResult.data.pallet_codes) {
                    // Convert pallet_codes to array if it's an object
                    let palletCodes = [];
                    if (Array.isArray(warehouseSummaryResult.data.pallet_codes)) {
                        palletCodes = warehouseSummaryResult.data.pallet_codes;
                    } else {
                        palletCodes = Object.values(warehouseSummaryResult.data.pallet_codes);
                    }
                    
                    logger.log('🔄 Transformed pallet codes to array:', palletCodes.length, 'pallets');
                    
                    // Debug: Log the first pallet to see available fields
                    if (palletCodes.length > 0) {
                        logger.log('🔍 DEBUG: First pallet from API:', palletCodes[0]);
                        logger.log('🔍 DEBUG: Available pallet fields:', Object.keys(palletCodes[0]));
                    }
                    
                    palletCodes.forEach(pallet => {
                        const laneId = pallet.lane_id || 'Unknown';
                        if (!transformedLanes[laneId]) {
                            transformedLanes[laneId] = {
                                lane_id: laneId,
                                pallets: []
                            };
                        }
                        transformedLanes[laneId].pallets.push({
                            id: pallet.pallet_id || pallet.pallet_code,
                            pallet_code: pallet.pallet_code,
                            pallet_type: pallet.pallet_type || null,
                            lane_id: laneId,
                            customer_id: parseInt(customerId),
                            qr_link: pallet.pallet_qr_link,
                            status: 'active',
                            created_at: pallet.created_at || new Date().toISOString(),
                            // Add any additional pallet data needed for full functionality
                            items: [] // Will be populated when product is selected - MUST be 'items' not 'product_items'
                        });
                    });
                }
                
                // Check if request was aborted before setting state
                if (abortSignal && abortSignal.aborted) {
                    throw new DOMException('Request was aborted', 'AbortError');
                }
                
                // Set the transformed data
                setWarehouseLanes(transformedLanes);
                setWarehouseProducts(transformedProducts);
                logger.log('✅ warehouse lanes loaded:', Object.keys(transformedLanes).length, transformedLanes);
                                
            } else {
                logger.warn('⚠️ Failed to load customer warehouse summary:', warehouseSummaryResult.error);
                setWarehouseLanes({});
                setWarehouseProducts([]);
                
                this.showAlert({
                    message: warehouseSummaryResult.error || 'Failed to load warehouse data for selected customer',
                    type: 'warning',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
            
        } catch (error) {
            if (error.name === 'AbortError') {
                logger.log('🚫 Customer data request was cancelled');
                // Don't show alert for cancelled requests
                return;
            }
            
            logger.error('❌ Error loading selected customer warehouse data:', error);
            setWarehouseLanes({});
            setWarehouseProducts([]);
            
            this.showAlert({
                message: 'Network error while loading customer warehouse data',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            // Only set loading to false if request wasn't aborted
            if (!abortSignal || !abortSignal.aborted) {
                setIsLoading(false);
            }
        }
    }

    async loadProductPalletDetails(productId, customerId, setIsLoading, setWarehouseLanes, productInfo = null, setAllCustomerPallets = null) {
        /**
         * Load detailed pallet information for a specific product under the selected customer
         * This is called when a product is selected from the product overview
         */
        logger.log('🔍 Function received - productId:', productId, 'customerId:', customerId, 'productInfo:', productInfo);
        
        if (!productId || !customerId) {
            logger.warn('⚠️ Cannot load product pallet details: missing product or customer ID');
            logger.warn('🔍 Debug - productId:', productId, 'customerId:', customerId);
            return;
        }

        setIsLoading(true);
        
        try {
            logger.log('🔄 Loading comprehensive pallet data for product:', productId, 'customer:', customerId);
            
            const { getSessionUUID } = await import('../../components/lib/AuthToken');
            const sessionToken = await getSessionUUID();
            
            // Fetch BOTH specific product pallets AND complete customer warehouse summary
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);
            
            const [productPalletsResponse, warehouseSummaryResult] = await Promise.all([
                // Get pallets containing this specific product
                fetch('/enhanced_warehouse/warehouse/product-pallets', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        product_id: productId,
                        customer_id: customerId
                    }),
                    signal: controller.signal
                }),
                // Get ALL customer pallets with occupancy data
                getCustomerWarehouseSummary(parseInt(customerId))
            ]);
            
            clearTimeout(timeoutId);
            
            if (!productPalletsResponse.ok) {
                if (productPalletsResponse.status === 504 || productPalletsResponse.status === 524) {
                    throw new Error('Request timeout - this product has a large dataset. Please try again.');
                }
                throw new Error(`HTTP ${productPalletsResponse.status}: ${productPalletsResponse.statusText}`);
            }
            
            const productPalletsData = await productPalletsResponse.json();
            
            if (!productPalletsData.success) {
                throw new Error(productPalletsData.error || 'Failed to fetch product pallet data');
            }
            
            logger.log('✅ Received product pallet data:', productPalletsData);
            logger.log('✅ Received warehouse summary data:', warehouseSummaryResult);
            
            // DEBUG: Check if pending_order field is in the API response
            logger.info('🔍 RAW API RESPONSE STRUCTURE:', {
                success: productPalletsData.success,
                has_data: !!productPalletsData.data,
                has_pallets: !!(productPalletsData.data && productPalletsData.data.pallets),
                pallets_count: productPalletsData.data?.pallets?.length || 0,
                first_pallet_fields: productPalletsData.data?.pallets?.[0] ? Object.keys(productPalletsData.data.pallets[0]) : 'No pallets',
                first_pallet_pending_order: productPalletsData.data?.pallets?.[0]?.pending_order || 'Missing'
            });
            
            // Process the data from both sources
            const { pallets: productSpecificPallets, product: productData } = productPalletsData.data;
            
            // Create maps for pallet_id -> quantity and pallet_id -> pending_order for pallets containing this product
            const productQuantityMap = {};
            const productPendingOrderMap = {};
            if (productSpecificPallets && productSpecificPallets.length > 0) {
                productSpecificPallets.forEach(pallet => {
                    productQuantityMap[pallet.pallet_id] = pallet.quantity;
                    productPendingOrderMap[pallet.pallet_id] = pallet.pending_order || false;
                });
                
                logger.log('🔍 PRODUCT SPECIFIC PALLETS - First 5 pallets:');
                productSpecificPallets.slice(0, 5).forEach((pallet, index) => {
                    logger.log(`  Product Pallet ${index + 1}:`);
                    logger.log(`    - pallet_code: ${pallet.pallet_code}`);
                    logger.log(`    - pallet_type: ${pallet.pallet_type}`);
                    logger.log(`    - lane_id: ${pallet.lane_id}`);
                    logger.log(`    - quantity: ${pallet.quantity}`);
                    logger.log(`    - pending_order: ${pallet.pending_order}`);
                });
            }
            
            // Get ALL customer pallets with occupancy data from warehouse summary
            const warehouseLanes = {};
            let totalQuantity = 0;
            
            if (warehouseSummaryResult.success && warehouseSummaryResult.data && warehouseSummaryResult.data.pallet_locations) {
                logger.log('🔍 FILTERING TO LANES WITH SELECTED PRODUCT');
                
                // Convert pallet_locations to array if it's an object
                let allCustomerPallets = [];
                if (Array.isArray(warehouseSummaryResult.data.pallet_locations)) {
                    allCustomerPallets = warehouseSummaryResult.data.pallet_locations;
                } else {
                    allCustomerPallets = Object.values(warehouseSummaryResult.data.pallet_locations);
                }
                
                logger.log(`🔍 Found ${allCustomerPallets.length} total customer pallets from warehouse summary`);
                
                // Store all customer pallets for product movement functionality
                if (setAllCustomerPallets) {
                    const transformedAllPallets = allCustomerPallets.map(pallet => ({
                        id: pallet.pallet_id,
                        pallet_code: pallet.pallet_code,
                        pallet_type: pallet.pallet_type || 'standard',
                        lane_id: pallet.lane_id,
                        customer_id: parseInt(customerId),
                        qr_link: pallet.pallet_qr_link,
                        status: pallet.status || 'active',
                        created_at: pallet.created_at,
                        occupied: pallet.occupied || false,
                        total_quantity: pallet.total_quantity || 0
                    }));
                    setAllCustomerPallets(transformedAllPallets);
                    logger.log(`📦 Stored ${transformedAllPallets.length} customer pallets for product movement`);
                }
                
                // First, identify which lanes contain this specific product
                const lanesWithProduct = new Set();
                if (productSpecificPallets && productSpecificPallets.length > 0) {
                    productSpecificPallets.forEach(pallet => {
                        if (pallet.lane_id) {
                            lanesWithProduct.add(pallet.lane_id);
                        }
                    });
                }
                
                logger.log(`🎯 Found ${lanesWithProduct.size} lanes containing this product:`, Array.from(lanesWithProduct));
                
                // Group pallets by lane_id - show ONLY pallets that actually contain this product
                const laneGroups = {};
                allCustomerPallets.forEach(pallet => {
                    const laneId = pallet.lane_id || 'Unknown Lane';
                    const palletId = pallet.pallet_id;
                    
                    // Only include pallets that actually contain this specific product
                    if (lanesWithProduct.has(laneId) && productQuantityMap.hasOwnProperty(palletId)) {
                        if (!laneGroups[laneId]) {
                            laneGroups[laneId] = [];
                        }
                        laneGroups[laneId].push(pallet);
                    }
                });

                // Catch any pallets in productSpecificPallets that the warehouse summary missed
                // (e.g. pallets whose customer_id doesn't match due to stale data)
                productSpecificPallets.forEach(pallet => {
                    const laneId = pallet.lane_id || 'Unknown Lane';
                    const palletId = pallet.pallet_id;
                    const alreadyAdded = laneGroups[laneId]?.some(p => p.pallet_id === palletId);
                    if (!alreadyAdded) {
                        if (!laneGroups[laneId]) laneGroups[laneId] = [];
                        // Normalise field names to match pallet_locations shape
                        laneGroups[laneId].push({
                            pallet_id: palletId,
                            pallet_code: pallet.pallet_code,
                            pallet_type: pallet.pallet_type || 'standard',
                            lane_id: laneId,
                            status: pallet.status || 'active',
                            created_at: pallet.created_at,
                            pallet_qr_link: pallet.qr_link,
                            occupied: true,
                            total_quantity: pallet.quantity || 0,
                        });
                    }
                });
                
                logger.log(`🎯 Showing ${Object.keys(laneGroups).length} lanes with ${Object.values(laneGroups).flat().length} pallets containing this product`);
                
                // Transform each lane group into the expected warehouse format
                Object.entries(laneGroups).forEach(([laneId, lanePallets]) => {
                    warehouseLanes[laneId] = {
                        lane_id: laneId,
                        pallets: lanePallets.map(pallet => {
                            const palletId = pallet.pallet_id;
                            const hasThisProduct = productQuantityMap.hasOwnProperty(palletId);
                            const productQuantity = productQuantityMap[palletId] || 0;
                            const productPendingOrder = productPendingOrderMap[palletId] || false;
                            
                            if (hasThisProduct) {
                                totalQuantity += productQuantity;
                            }
                            
                            logger.log(`🔍 PROCESSING PALLET: ${pallet.pallet_code} - occupied: ${pallet.occupied}, hasThisProduct: ${hasThisProduct}, quantity: ${productQuantity}, pending_order: ${productPendingOrder}`);
                            
                            return {
                                id: palletId,
                                pallet_code: pallet.pallet_code,
                                pallet_type: pallet.pallet_type || 'standard',
                                lane_id: laneId,
                                customer_id: parseInt(customerId),
                                qr_link: pallet.pallet_qr_link,
                                status: pallet.status || 'active',
                                created_at: pallet.created_at,
                                product_quantity: productQuantity,
                                occupied: pallet.occupied || false,
                                total_quantity: pallet.total_quantity || 0,
                                items: hasThisProduct ? [{
                                    id: `${palletId}_${productId}`,
                                    product_id: parseInt(productId),
                                    quantity: productQuantity,
                                    pending_order: productPendingOrder,
                                    product: productData ? {
                                        id: productData.id,
                                        product_code: productData.product_code,
                                        product_name: productData.product_name,
                                        batch_no: productData.batch_no,
                                        customer_id: productData.customer_id,
                                        qr_link: productData.qr_link,
                                        created_at: productData.created_at,
                                        created_date: productData.created_date || productData.created_at?.split('T')[0]
                                    } : {
                                        id: parseInt(productId),
                                        product_code: `Product ${productId}`,
                                        product_name: 'Product Information Unavailable',
                                        batch_no: null,
                                        customer_id: parseInt(customerId),
                                        qr_link: null,
                                        created_date: new Date().toISOString().split('T')[0]
                                    }
                                }] : []
                            };
                        })
                    };
                    
                    // Sort pallets within lane by pallet code
                    warehouseLanes[laneId].pallets.sort((a, b) => a.pallet_code.localeCompare(b.pallet_code));
                });
            } else {
                logger.log('⚠️ Warehouse summary data not available, falling back to product-specific pallets only');
                // Fallback to product-specific pallets only if warehouse summary is unavailable
                if (productSpecificPallets && productSpecificPallets.length > 0) {
                    const laneGroups = {};
                    productSpecificPallets.forEach(pallet => {
                        const laneId = pallet.lane_id || 'Unknown Lane';
                        if (!laneGroups[laneId]) {
                            laneGroups[laneId] = [];
                        }
                        laneGroups[laneId].push(pallet);
                        totalQuantity += (pallet.quantity || 0);
                    });
                    
                    Object.entries(laneGroups).forEach(([laneId, lanePallets]) => {
                        warehouseLanes[laneId] = {
                            lane_id: laneId,
                            pallets: lanePallets.map(pallet => ({
                                id: pallet.pallet_id,
                                pallet_code: pallet.pallet_code,
                                pallet_type: pallet.pallet_type || 'standard',
                                lane_id: laneId,
                                customer_id: parseInt(customerId),
                                qr_link: pallet.qr_link,
                                status: pallet.status || 'active',
                                created_at: pallet.created_at,
                                product_quantity: pallet.quantity,
                                occupied: true, // These pallets have this product, so they're occupied
                                items: [{
                                    id: `${pallet.pallet_id}_${productId}`,
                                    product_id: parseInt(productId),
                                    quantity: pallet.quantity,
                                    product: productData || {}
                                }]
                            }))
                        };
                        warehouseLanes[laneId].pallets.sort((a, b) => a.pallet_code.localeCompare(b.pallet_code));
                    });
                }
            }
            
            setWarehouseLanes(warehouseLanes);
            
            const laneCount = Object.keys(warehouseLanes).length;
            const totalPallets = Object.values(warehouseLanes).reduce((sum, lane) => sum + lane.pallets.length, 0);
            const palletsWithProduct = Object.values(warehouseLanes).reduce((sum, lane) => 
                sum + lane.pallets.filter(p => p.product_quantity > 0).length, 0);
            const occupiedPallets = Object.values(warehouseLanes).reduce((sum, lane) => 
                sum + lane.pallets.filter(p => p.occupied).length, 0);
            const availablePallets = totalPallets - occupiedPallets;
            
            logger.log('✅ Enhanced product warehouse view loaded successfully:');
            logger.log('📊 Summary:', {
                product_code: productData?.product_code || `Product ${productId}`,
                product_name: productData?.product_name || 'Unknown',
                lanes: laneCount,
                total_pallets: totalPallets,
                pallets_with_this_product: palletsWithProduct,
                occupied_pallets: occupiedPallets,
                available_pallets: availablePallets,
                total_quantity_of_this_product: totalQuantity,
                customer_id: customerId
            });
            
        } catch (error) {
            logger.error('❌ Error loading product pallet details:', error);
            setWarehouseLanes({});
            
            let errorMessage = 'Failed to load warehouse locations for this product';
            
            if (error.name === 'AbortError') {
                errorMessage = 'Request timed out - this product has a large dataset. The system is processing it in the background. Please try again in a moment.';
            } else if (error.message.includes('timeout')) {
                errorMessage = error.message;
            }
            
            this.showAlert({
                message: errorMessage,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsLoading(false);
        }
    }


    async generateQRCode(type, data, refreshWarehouseData, setIsGeneratingQR) {
        const key = `${type}-${data.id || data.product_code}`;
        setIsGeneratingQR(prev => ({ ...prev, [key]: true }));
        
        try {
            let qrData;
            if (type === 'pallet') {
                qrData = {
                    type: 'pallets',
                    pallet_code: data.pallet_code,
                    lane_id: data.lane_id,
                    created_date: data.created_date
                };
            } else {
                qrData = {
                    type: 'products',
                    product_code: data.product_code,
                    product_name: data.product_name,
                    customer_id: data.customer_id,
                    created_date: data.created_date || new Date().toISOString().split('T')[0]
                };
            }

            const response = await generateEnhancedQRCode(data.id, qrData.type, qrData);

            if (response.status === 200) {
                // Refresh warehouse data after successful QR generation
                await refreshWarehouseData();
                
                this.showAlert({
                    message: `QR Code generated successfully for ${type}`,
                    type: "success",
                    buttons: [{ label: "Close", type: "primary" }],
                });
            } else {
                throw new Error(response.message || 'Failed to generate QR code');
            }
        } catch (error) {
            logger.error('Error generating QR code:', error);
            this.showAlert({
                message: `Failed to generate QR code: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
        } finally {
            setIsGeneratingQR(prev => ({ ...prev, [key]: false }));
        }
    }

    async submitInboundLoad(inboundData, userId) {
        try {
            const response = await inboundProducts(inboundData, userId);

            if (response.status === 200 || response.status === 201) {
                this.showAlert({
                    message: 'Inbound load submitted successfully',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                // Note: Data refresh should be handled by the parent component
                return { status: response.status };
            } else {
                throw new Error(response.message || 'Failed to submit inbound load');
            }
        } catch (error) {
            logger.error('Error submitting inbound load:', error);
            this.showAlert({
                message: `Failed to submit inbound load: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return { status: 500 };
        }
    }

    async downloadCharges(date) {
        const {status} = await download_warehouse_charges(date);
        logger.log("Download status: ", status);
        if (status !== 200) {
            this.showAlert({
                message: 'An error occurred while downloading data.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
    }

    async invoiceCharges(date, accountCodeId) {
        const response = await invoice_warehouse_charges(date, accountCodeId);
        if (![200, 201].includes(response.status)) {
            this.showAlert({
                message: response.data?.error || 'Unable to generate warehouse invoice drafts.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        const draftCount = response.data?.draft_count || 0;
        const exceptionCount = response.data?.exceptions?.length || 0;
        this.showAlert({
            message: `${draftCount} warehouse invoice draft${draftCount === 1 ? '' : 's'} created.${exceptionCount ? ` ${exceptionCount} charge${exceptionCount === 1 ? '' : 's'} skipped.` : ''}`,
            type: draftCount ? 'success' : 'warning',
            buttons: [{ type: 'primary', label: 'Close' }],
        });
    }

    /**
     * Update customer name based on selected customer ID
     * Used when admin/warehouse users select a customer from dropdown
     */
    updateCustomerName(customerId, customers, setCustomerName) {
        if (!customerId || !customers || !setCustomerName) {
            if (setCustomerName) setCustomerName(null);
            return;
        }

        // Find the customer name from the customers array
        const customer = customers.find(c => c.id?.toString() === customerId?.toString());
        const customerName = customer?.name || null;
        

        setCustomerName(customerName);
    }

    async refreshData(setIsLoading, setCustomers, setSelectedCustomer, setWarehouseLanes, setWarehouseProducts, setPendingOrdersCount, restricted, customerRestricted, logout, navigate, profile, isCustomer, setWarehouseSummaryProducts, setAllCustomerPallets, currentSelectedCustomer = null) {
        // Reuse the loadPageData function for refresh
        await this.loadPageData(setIsLoading, setCustomers, setSelectedCustomer, setWarehouseLanes, setWarehouseProducts, setPendingOrdersCount, restricted, customerRestricted, logout, navigate, profile, isCustomer, setWarehouseSummaryProducts, setAllCustomerPallets, currentSelectedCustomer);
    }

    async generateHistoricalStockReport(customerId, stockDate) {
        return downloadHistoricalStockReport(
            stockDate,
            customerId
        );
    };
}